/**
 * Budget Dashboard Tab Component
 *
 * Comprehensive budget tracking and cost analysis interface for projects
 *
 * Features:
 * - Budget configuration and editing
 * - Real-time budget utilization visualization
 * - Cost breakdown by type (pie chart)
 * - Daily cost trends (bar chart)
 * - Detailed cost records table
 * - Export functionality
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 * Tim-Combo Pattern: .equilateral-standards/frontend-development/tim_combo_frontend_auth_pattern.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Alert, Spinner, TextInput, Label, Modal } from 'flowbite-react';
import {
  HiCurrencyDollar,
  HiPencil,
  HiDownload,
  HiRefresh,
  HiExclamationCircle
} from 'react-icons/hi';

import {
  getProjectBudget,
  updateProjectBudget,
  getProjectCostsDetailed,
  type ProjectBudget,
  type ProjectCostByType,
  type ProjectDailyCost,
  type ProjectCostDetail,
  type UpdateBudgetRequest
} from '../api/projects';

import CostBreakdownPieChart from './charts/CostBreakdownPieChart';
import DailyCostsBarChart from './charts/DailyCostsBarChart';
import BudgetUtilizationBar from './charts/BudgetUtilizationBar';

export interface BudgetDashboardTabProps {
  clientId: string;
  companyId: string;
  projectId: string;
}

export const BudgetDashboardTab: React.FC<BudgetDashboardTabProps> = ({
  clientId,
  companyId,
  projectId
}) => {
  // State management
  const [budget, setBudget] = useState<ProjectBudget | null>(null);
  const [costsByType, setCostsByType] = useState<ProjectCostByType[]>([]);
  const [dailyCosts, setDailyCosts] = useState<ProjectDailyCost[]>([]);
  const [costDetails, setCostDetails] = useState<ProjectCostDetail[]>([]);

  const [isLoadingBudget, setIsLoadingBudget] = useState(true);
  const [isLoadingCosts, setIsLoadingCosts] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Budget form state
  const [budgetForm, setBudgetForm] = useState({
    Monthly_Budget: '',
    Alert_Threshold: '80',
    Budget_Period_Start: '',
    Budget_Period_End: ''
  });

  // Load budget data
  const loadBudget = useCallback(async () => {
    try {
      setIsLoadingBudget(true);
      setError(null);

      const response = await getProjectBudget(clientId, companyId, projectId);

      if (!response.success) {
        throw new Error(response.message || 'Failed to load budget');
      }

      const budgetData = response.data.Records[0];
      setBudget(budgetData);

      // Initialize form if budget is configured
      if (budgetData.Budget_Configured) {
        setBudgetForm({
          Monthly_Budget: budgetData.Monthly_Budget?.toString() || '',
          Alert_Threshold: budgetData.Alert_Threshold.toString(),
          Budget_Period_Start: budgetData.Budget_Period_Start || '',
          Budget_Period_End: budgetData.Budget_Period_End || ''
        });
      }
    } catch (err) {
      console.error('[BudgetDashboardTab] Error loading budget:', err);
      setError(err instanceof Error ? err.message : 'Failed to load budget');
    } finally {
      setIsLoadingBudget(false);
    }
  }, [clientId, companyId, projectId]);

  // Load cost data
  const loadCosts = useCallback(async () => {
    try {
      setIsLoadingCosts(true);
      setError(null);

      // Get last 30 days of data
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      // Load costs by type (for pie chart)
      const costsByTypeResponse = await getProjectCostsDetailed({
        Client_ID: clientId,
        Company_ID: companyId,
        Project_ID: projectId,
        group_by: 'type',
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      });

      if (costsByTypeResponse.success) {
        setCostsByType(costsByTypeResponse.data.Records as ProjectCostByType[]);
      }

      // Load daily costs (for bar chart)
      const dailyCostsResponse = await getProjectCostsDetailed({
        Client_ID: clientId,
        Company_ID: companyId,
        Project_ID: projectId,
        group_by: 'daily',
        limit: 30
      });

      if (dailyCostsResponse.success) {
        setDailyCosts(dailyCostsResponse.data.Records as ProjectDailyCost[]);
      }

      // Load recent cost details (for table)
      const detailsResponse = await getProjectCostsDetailed({
        Client_ID: clientId,
        Company_ID: companyId,
        Project_ID: projectId,
        group_by: 'detail',
        limit: 50
      });

      if (detailsResponse.success) {
        setCostDetails(detailsResponse.data.Records as ProjectCostDetail[]);
      }
    } catch (err) {
      console.error('[BudgetDashboardTab] Error loading costs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load cost data');
    } finally {
      setIsLoadingCosts(false);
    }
  }, [clientId, companyId, projectId]);

  // Initial load
  useEffect(() => {
    loadBudget();
    loadCosts();
  }, [loadBudget, loadCosts]);

  // Handle budget save
  const handleSaveBudget = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Validation
      if (!budgetForm.Monthly_Budget || parseFloat(budgetForm.Monthly_Budget) <= 0) {
        setError('Monthly budget must be a positive number');
        return;
      }

      if (!budgetForm.Budget_Period_Start || !budgetForm.Budget_Period_End) {
        setError('Budget period start and end dates are required');
        return;
      }

      const budgetData: UpdateBudgetRequest = {
        Client_ID: clientId,
        Company_ID: companyId,
        Project_ID: projectId,
        Monthly_Budget: parseFloat(budgetForm.Monthly_Budget),
        Alert_Threshold: parseFloat(budgetForm.Alert_Threshold),
        Budget_Period_Start: budgetForm.Budget_Period_Start,
        Budget_Period_End: budgetForm.Budget_Period_End
      };

      const response = await updateProjectBudget(projectId, budgetData);

      if (!response.success) {
        throw new Error(response.message || 'Failed to update budget');
      }

      setShowBudgetModal(false);
      await loadBudget(); // Reload to get updated utilization
    } catch (err) {
      console.error('[BudgetDashboardTab] Error saving budget:', err);
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    } finally {
      setIsSaving(false);
    }
  };

  // Export costs to CSV
  const exportCostsToCSV = () => {
    if (costDetails.length === 0) {
      alert('No cost data to export');
      return;
    }

    const headers = ['Date', 'Type', 'Amount', 'Agent', 'Execution ID'];
    const rows = costDetails.map(cost => [
      cost.Cost_Date,
      cost.Cost_Type,
      cost.Cost_Amount.toFixed(4),
      cost.Agent_Name || '-',
      cost.Execution_ID || '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `project-costs-${projectId}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoadingBudget && isLoadingCosts) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          <div className="flex items-center">
            <HiExclamationCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </Alert>
      )}

      {/* Budget Overview Card */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <HiCurrencyDollar className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Budget Overview</h3>
          </div>
          <div className="flex space-x-2">
            <Button size="sm" color="light" onClick={() => { loadBudget(); loadCosts(); }}>
              <HiRefresh className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setShowBudgetModal(true)}>
              <HiPencil className="w-4 h-4 mr-2" />
              {budget?.Budget_Configured ? 'Edit Budget' : 'Configure Budget'}
            </Button>
          </div>
        </div>

        {budget?.Budget_Configured ? (
          <BudgetUtilizationBar
            currentSpend={budget.Current_Spend}
            monthlyBudget={budget.Monthly_Budget || 0}
            utilizationPercentage={budget.Utilization_Percentage}
            alertThreshold={budget.Alert_Threshold}
            alertTriggered={budget.Alert_Triggered}
          />
        ) : (
          <Alert color="info">
            <p className="text-sm">
              No budget configured for this project. Click "Configure Budget" to set up budget tracking.
            </p>
          </Alert>
        )}
      </Card>

      {/* Cost Breakdown and Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Breakdown Pie Chart */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Cost Breakdown by Type
          </h3>
          <CostBreakdownPieChart data={costsByType} isLoading={isLoadingCosts} />
        </Card>

        {/* Daily Cost Trends */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Daily Cost Trends (Last 30 Days)
          </h3>
          <DailyCostsBarChart data={dailyCosts} isLoading={isLoadingCosts} />
        </Card>
      </div>

      {/* Cost Details Table */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Costs</h3>
          <Button size="sm" color="light" onClick={exportCostsToCSV} disabled={costDetails.length === 0}>
            <HiDownload className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {isLoadingCosts ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : costDetails.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No cost records found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Agent</th>
                  <th className="px-4 py-3">Execution ID</th>
                </tr>
              </thead>
              <tbody>
                {costDetails.map((cost) => (
                  <tr key={cost.Cost_ID} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {new Date(cost.Cost_Date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {cost.Cost_Type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">
                      ${cost.Cost_Amount.toFixed(4)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {cost.Agent_Name || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                      {cost.Execution_ID ? cost.Execution_ID.substring(0, 12) + '...' : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Budget Configuration Modal */}
      <Modal show={showBudgetModal} onClose={() => setShowBudgetModal(false)} size="md">
        <Modal.Header>
          {budget?.Budget_Configured ? 'Edit Budget' : 'Configure Budget'}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="monthly_budget">Monthly Budget ($)</Label>
              <TextInput
                id="monthly_budget"
                type="number"
                step="0.01"
                min="0"
                value={budgetForm.Monthly_Budget}
                onChange={(e) => setBudgetForm({ ...budgetForm, Monthly_Budget: e.target.value })}
                placeholder="1000.00"
                required
              />
            </div>

            <div>
              <Label htmlFor="alert_threshold">Alert Threshold (%)</Label>
              <TextInput
                id="alert_threshold"
                type="number"
                step="1"
                min="0"
                max="100"
                value={budgetForm.Alert_Threshold}
                onChange={(e) => setBudgetForm({ ...budgetForm, Alert_Threshold: e.target.value })}
                helperText="Alert will trigger when spending reaches this percentage of budget"
              />
            </div>

            <div>
              <Label htmlFor="period_start">Budget Period Start</Label>
              <TextInput
                id="period_start"
                type="date"
                value={budgetForm.Budget_Period_Start}
                onChange={(e) => setBudgetForm({ ...budgetForm, Budget_Period_Start: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="period_end">Budget Period End</Label>
              <TextInput
                id="period_end"
                type="date"
                value={budgetForm.Budget_Period_End}
                onChange={(e) => setBudgetForm({ ...budgetForm, Budget_Period_End: e.target.value })}
                required
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSaveBudget} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              'Save Budget'
            )}
          </Button>
          <Button color="gray" onClick={() => setShowBudgetModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BudgetDashboardTab;
